import type { Auth0User } from 'paintscout'
import { USER_COLOR_PALETTE } from '@paintscout/util'

/**
 * Builds the `Auth0User` object passed to `<UserProvider>` by overlaying the live
 * `companyDetails` (from the `appDocs` query) onto the session's token-derived user.
 *
 * `companyDetails` is authoritative for `user.app_metadata.company`: spreading
 * `session.app_metadata.company` under it would leak fields from the logged-in
 * superadmin into a masqueraded view — any field that happens to be unset on the
 * masqueraded company's meta document is `undefined` on `companyDetails`, gets
 * stripped by JSON transport, and therefore cannot override a truthy value
 * inherited from the superadmin's token (e.g. `nonBilling: true` on paintscout's
 * internal admin company hiding the Billing menu for the masqueraded paying
 * customer).
 *
 * If `companyDetails` is missing (shouldn't happen for a well-formed appDocs
 * response), fall back to the session's company so downstream consumers still
 * have a shape to read from.
 */
export function buildUserForProvider({
  session,
  companyDetails
}: {
  session: Auth0User
  companyDetails: Auth0User['app_metadata']['company'] | null | undefined
}): Auth0User {
  return {
    ...session,
    app_metadata: {
      ...session?.app_metadata,
      company: companyDetails ?? session?.app_metadata?.company
    },
    user_metadata: {
      ...session?.user_metadata,
      color: session?.user_metadata?.color || USER_COLOR_PALETTE[0]
    }
  } as Auth0User
}
